import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../../lib/api";
import type { Person } from "../../lib/api-types";
import { paths } from "../../lib/endpoints";
import { invalidateWorkspaceData } from "../workspace/invalidate";

export interface PersonInput {
  name: string;
  number: number | null;
}

export interface PersonPatch {
  name?: string;
  number?: number | null;
  photo_locked?: boolean;
  song_locked?: boolean;
  photo_media_id?: string | null;
}

/** Response of the one-step invite: the new person and their player link. */
export interface PlayerInviteResult {
  person: Person;
  url: string;
}

/** Operator actions on one workspace's roster. Every success refreshes derived data. */
export function usePersonMutations(workspaceId: string) {
  const queryClient = useQueryClient();
  const refresh = () => invalidateWorkspaceData(queryClient, workspaceId);

  const create = useMutation({
    mutationFn: (input: PersonInput) =>
      apiRequest<Person>(paths.persons(workspaceId), { method: "POST", body: input }),
    onSuccess: refresh,
  });

  const invite = useMutation({
    mutationFn: (input: PersonInput) =>
      apiRequest<PlayerInviteResult>(paths.personInvite(workspaceId), {
        method: "POST",
        body: input,
      }),
    onSuccess: refresh,
  });

  const update = useMutation({
    mutationFn: ({ personId, patch }: { personId: string; patch: PersonPatch }) =>
      apiRequest<Person>(paths.person(workspaceId, personId), { method: "PATCH", body: patch }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (personId: string) =>
      apiRequest<{ deleted: boolean }>(paths.person(workspaceId, personId), { method: "DELETE" }),
    onSuccess: refresh,
  });

  const restore = useMutation({
    mutationFn: (personId: string) =>
      apiRequest<{ restored: boolean }>(paths.personRestore(workspaceId, personId), {
        method: "POST",
      }),
    onSuccess: refresh,
  });

  /** Creates or replaces the player link. The returned URL is shown by the caller. */
  const regenerateLink = useMutation({
    mutationFn: (personId: string) =>
      apiRequest<{ url: string }>(paths.personLink(workspaceId, personId), { method: "POST" }),
    onSuccess: refresh,
  });

  return { create, invite, update, remove, restore, regenerateLink };
}
